import { BehaviorSubject } from 'rxjs';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { isAfter, isToday } from 'date-fns';


export type PickerMode = 'month' | 'year' | 'day';

const formatDate = (date: Date) => {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();
  if (month.length < 2) {
    month = '0' + month;
  }
  if (day.length < 2) {
    day = '0' + day;
  }
  return [year, month, day].join('-');
};

@Component({
  selector: 'calendar-picker',
  templateUrl: './calendar-picker.component.html',
  styleUrls: ['./calendar-picker.component.scss'],
})
export class CalendarPickerComponent {
  @Input() startView: 'month' | 'year' | 'multi-year' = 'multi-year';
  @Input() disabled = false;
  @Input() disableFutureDates = true;
  readonly mode$ = new BehaviorSubject<PickerMode>('day');
  @Input() set mode(mode: PickerMode | undefined | null) {
    if (mode) {
      this.mode$.next(mode);
      this.date.setValue(formatDate(new Date()));
      this.dateSelected.next(this.date.value);
    }
  }
  get mode(): PickerMode {
    return this.mode$.value;
  }

  @Output() dateSelected = new EventEmitter<string>();

  readonly maxDate = new Date();

  get selectedDate() {
    const segments = this.date.value.split('-');
    switch (this.mode) {
      case 'day':
        return { value: this.date.value };
      case 'month':
        return { value: `${segments[0]}-${segments[1]}` };
      case 'year':
        return { value: segments[0] };
    }
  }

  get isButtonDisabled(): boolean {
    const now = new Date();

    if (this.mode === 'day') {
      return isToday(new Date(this.date.value)) || isAfter(new Date(this.date.value), now);
    } else if (this.mode === 'month') {
      return now.getMonth() === new Date(this.date.value).getMonth();
    } else if (this.mode === 'year') {
      return now.getFullYear() === new Date(this.date.value).getFullYear();
    }

    return false;
  }


  readonly date = new UntypedFormControl(formatDate(new Date()));

  incrementDate() {
    if (this.mode === 'day') {
    let date = new Date(this.date.value);
    date.setDate(date.getDate() + 1);
    this.date.setValue(formatDate(date));
    }
    if(this.mode === 'month') {
      let date = new Date(this.date.value);
      date.setMonth(date.getMonth() + 1);
      this.date.setValue(formatDate(date));
    }
    if(this.mode === 'year') {
      let date = new Date(this.date.value);
      date.setFullYear(date.getFullYear() + 1);
      this.date.setValue(formatDate(date));
    }
    this.dateSelected.next(this.date.value);
  }

  decrementDate() {
    if (this.mode === 'day') {
      let date = new Date(this.date.value);
      date.setDate(date.getDate() - 1);
      this.date.setValue(formatDate(date));
    }
    if(this.mode === 'month') {
      let date = new Date(this.date.value);
      date.setMonth(date.getMonth() - 1);
      this.date.setValue(formatDate(date));
    }
    if(this.mode === 'year') {
      let date = new Date(this.date.value);
      date.setFullYear(date.getFullYear() - 1);
      this.date.setValue(formatDate(date));
    }
    this.dateSelected.next(this.date.value);
  }

  setDate(input: MatDatepickerInputEvent<Date>) {
    if (input.value) {
      this.date.setValue(formatDate(input.value));
      this.dateSelected.next(this.date.value);
    }
  }

  setMonth(selected: Date, datepicker: MatDatepicker<Date>) {
    if (this.mode === 'month') {
      const ctrlValue = new Date(this.date.value);
      ctrlValue.setFullYear(selected.getFullYear());
      ctrlValue.setMonth(selected.getMonth());
      this.date.setValue(formatDate(ctrlValue));
      document.querySelector('mat-calendar')?.classList.add('d-none');
      datepicker.close();
      this.dateSelected.next(this.date.value);
    }
  }

  setYear(selected: Date, datepicker: MatDatepicker<Date>) {
    if (this.mode === 'year') {
      const ctrlValue = new Date(this.date.value);
      ctrlValue.setFullYear(selected.getFullYear());
      this.date.setValue(formatDate(ctrlValue));
      document.querySelector('mat-calendar')?.classList.add('d-none');
      datepicker.close();
      this.dateSelected.next(this.date.value);
    }
  }
}
